import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleHelp } from "lucide-react";
import { cn } from "../lib/utils";

interface InfoTooltipProps {
  /** Izoh matni — bir-ikki gap (uzun matn uchun dialog qulayroq). */
  text: string;
  className?: string;
  /** Ko'pikni chapga tekislash (o'ng chekkadagi element uchun). */
  align?: "start" | "end";
}

/**
 * "?" tugmasi va bosilganda ochiladigan izoh.
 *
 * Loyihada tooltip primitivi yo'q (Radix ham yo'q) — hodisa tinglovchilari
 * `page-filter-popover.tsx` bilan bir xil uslubda: tashqariga bosish va Escape
 * yopadi. Hover EMAS, bosish orqali: sensorli ekranda hover yo'q va bu izoh
 * telefonda ham ochilishi kerak.
 *
 * Native `title` ham qoldirilgan — klaviatura/skriner uchun zaxira.
 */
export function InfoTooltip({ text, className, align = "start" }: InfoTooltipProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <span className={cn("relative inline-flex align-middle", className)} ref={rootRef}>
      <button
        type="button"
        onClick={(e) => {
          // Qatorga bosish (tovar analitikasi) yoki forma yuborilishi
          // qo'zg'atilmasligi kerak.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="text-muted-foreground hover:text-foreground transition-colors rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        aria-label={t("actions.help")}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        title={text}
      >
        <CircleHelp className="size-3.5" />
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className={cn(
            "absolute top-full mt-1.5 z-50 w-60 max-w-[calc(100vw-2rem)]",
            "rounded-md border bg-card text-card-foreground shadow-lg",
            "p-2.5 text-xs font-normal normal-case tracking-normal leading-relaxed",
            "animate-fade-in",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {text}
        </span>
      )}
    </span>
  );
}
